import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Hammer, Briefcase, MonitorSmartphone, FileCheck2, Award, Target,
  Clock, BarChart3, CheckCircle2, BadgeCheck, Quote,
} from "lucide-react";
import api, { waLink } from "../lib/api";
import { WHY_CARDS, CAPSTONE_STEPS, CAREER_ROLES, MARQUEE_ITEMS } from "../lib/data";
import { Reveal, Eyebrow } from "../components/Reveal";
import { EnquiryForm, EnquiryModal } from "../components/EnquiryModal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useTitle } from "../hooks/useTitle";

const ICONS = { Hammer, Briefcase, MonitorSmartphone, FileCheck2, Award, Target };

const HERO_LINES = [
  { text: "Master Tally, GST & TDS", cls: "text-white" },
  { text: "with practical,", cls: "text-white" },
  { text: "job-ready training.", cls: "text-[#C59B27]" },
];

const Hero = ({ courses }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
    <section ref={ref} className="relative bg-[#0A192F] overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 hero-grid" />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-[#0F5132]/30 blur-[140px]" />
      <div className="absolute bottom-0 left-1/3 w-[420px] h-[420px] rounded-full bg-[#C59B27]/10 blur-[120px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono-sn text-xs font-bold uppercase tracking-[0.25em] text-[#C59B27] mb-6"
            data-testid="hero-eyebrow"
          >
            Learn. Practice. Grow.
          </motion.p>
          <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            {HERO_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.cls}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed"
          >
            Learn accounting and taxation through hands-on training, real-world examples and a capstone project
            designed to build career-ready skills.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/courses"
              data-testid="hero-explore-courses-btn"
              className="px-7 py-3.5 rounded-full bg-[#C59B27] text-[#0A192F] font-display font-bold text-sm tracking-wide hover:bg-[#d8ae3a] hover:scale-[1.03] transition-all"
            >
              Explore Courses
            </Link>
            <EnquiryModal
              source="hero"
              trigger={
                <button
                  data-testid="hero-enquire-btn"
                  className="px-7 py-3.5 rounded-full border border-white/30 text-white font-display font-bold text-sm tracking-wide hover:border-[#C59B27] hover:text-[#C59B27] transition-colors"
                >
                  Enquire Now
                </button>
              }
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-semibold text-slate-400"
            data-testid="hero-trust-line"
          >
            {["Practical Learning", "Hands-on Assignments", "Career-focused Training"].map((t) => (
              <span key={t} className="flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-[#0F5132]" /> {t}
              </span>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="relative"
        >
          <div className="hidden xl:block absolute -left-10 -top-8 z-10 animate-float">
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3 text-white">
              <p className="font-mono-sn text-[10px] text-[#C59B27] tracking-widest">GSTR-3B</p>
              <p className="text-sm font-semibold flex items-center gap-1.5"><BadgeCheck size={14} className="text-[#25D366]" /> Filed on time</p>
            </div>
          </div>
          <div className="hidden xl:block absolute -right-6 -bottom-8 z-10 animate-float-slow">
            <div className="bg-white/10 backdrop-blur-md border border-white/15 rounded-xl px-4 py-3 text-white">
              <p className="font-mono-sn text-[10px] text-[#C59B27] tracking-widest">BANK REC</p>
              <p className="text-sm font-semibold flex items-center gap-1.5"><BadgeCheck size={14} className="text-[#25D366]" /> 100% matched</p>
            </div>
          </div>
          <motion.div style={{ y: imgY }} className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 border-t-4 border-[#C59B27]" data-testid="hero-enquiry-card">
              <h3 className="font-display font-extrabold text-xl text-[#0A192F]">Get Course Details</h3>
              <p className="text-sm text-[#627D98] mt-1 mb-5">Syllabus, batch timings & fees — straight to your phone.</p>
              <EnquiryForm source="homepage-hero" />
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <div className="bg-[#C59B27] py-3.5 overflow-hidden" data-testid="marquee-band">
    <div className="flex whitespace-nowrap animate-marquee w-max">
      {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
        <span key={i} className="font-mono-sn text-sm font-bold tracking-[0.2em] text-[#0A192F] mx-6 flex items-center gap-6">
          {item} <span className="text-[#0A192F]/50">✦</span>
        </span>
      ))}
    </div>
  </div>
);

const WhySection = () => (
  <section className="py-20 lg:py-28" data-testid="why-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>Why SterlingNest</Eyebrow>
        <h2 className="font-display font-extrabold tracking-tight text-[#0A192F] text-2xl sm:text-3xl lg:text-4xl mt-3 max-w-2xl">
          Accounting is a skill. Skills are built by doing.
        </h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {WHY_CARDS.map((card, i) => {
          const Icon = ICONS[card.icon];
          return (
            <Reveal key={card.title} delay={i * 0.08}>
              <div
                data-testid={`why-card-${i}`}
                className="group h-full bg-white rounded-2xl border border-slate-200 p-7 hover:border-[#0F5132]/50 hover:shadow-[0_16px_40px_rgba(10,25,47,0.08)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-[#E8F5E9] flex items-center justify-center group-hover:bg-[#0F5132] transition-colors duration-300">
                  <Icon size={20} className="text-[#0F5132] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-display font-bold text-lg text-[#0A192F] mt-5">{card.title}</h3>
                <p className="text-sm text-[#486581] leading-relaxed mt-2">{card.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const FeaturedCourses = ({ courses }) => (
  <section className="py-20 lg:py-28 bg-white border-y border-slate-200" data-testid="featured-courses-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Featured Courses</Eyebrow>
          <h2 className="font-display font-extrabold tracking-tight text-[#0A192F] text-2xl sm:text-3xl lg:text-4xl mt-3">
            Programs built for the job, not the exam.
          </h2>
        </div>
        <Link to="/courses" data-testid="view-all-courses-link" className="flex items-center gap-1.5 text-sm font-bold text-[#0F5132] hover:gap-3 transition-all">
          View all courses <ArrowRight size={16} />
        </Link>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {courses.map((c, i) => (
          <Reveal key={c.id} delay={i * 0.08}>
            <div
              data-testid={`course-card-${c.slug}`}
              className="group h-full flex flex-col bg-[#F8FAFC] rounded-2xl border border-slate-200 overflow-hidden hover:shadow-[0_20px_50px_rgba(10,25,47,0.12)] hover:-translate-y-1.5 transition-all duration-300"
            >
              <div className="relative h-40 overflow-hidden">
                <img src={c.image_url} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#0A192F]/90 text-[#C59B27] text-[10px] font-mono-sn font-bold tracking-wider">
                  {c.badge}
                </span>
              </div>
              <div className="p-5 flex flex-col flex-1">
                <h3 className="font-display font-bold text-base text-[#0A192F] leading-snug">{c.title}</h3>
                <div className="flex flex-wrap gap-2 mt-3 text-[11px] font-semibold text-[#334E68]">
                  <span className="flex items-center gap-1"><Clock size={11} className="text-[#0F5132]" /> {c.duration}</span>
                  <span className="flex items-center gap-1"><BarChart3 size={11} className="text-[#0F5132]" /> {c.level}</span>
                </div>
                <p className="text-xs text-[#627D98] leading-relaxed mt-3 flex-1">
                  {(c.modules || []).slice(0, 3).join(" • ")}
                </p>
                <div className="flex gap-2 mt-5">
                  <Link
                    to={`/courses/${c.slug}`}
                    data-testid={`course-view-${c.slug}`}
                    className="flex-1 text-center px-3 py-2 rounded-lg bg-[#0A192F] text-white text-xs font-bold hover:bg-[#0F5132] transition-colors"
                  >
                    View Course
                  </Link>
                  <EnquiryModal
                    defaultInterest={c.title}
                    source={`course-card-${c.slug}`}
                    trigger={
                      <button data-testid={`course-enquire-${c.slug}`} className="flex-1 px-3 py-2 rounded-lg border border-[#0A192F] text-[#0A192F] text-xs font-bold hover:bg-[#0A192F] hover:text-white transition-colors">
                        Enquire
                      </button>
                    }
                  />
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Capstone = () => (
  <section className="py-20 lg:py-28 bg-[#0A192F] relative overflow-hidden" data-testid="capstone-section">
    <div className="absolute inset-0 hero-grid opacity-60" />
    <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-[#0F5132]/25 blur-[140px]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-20">
        <div className="lg:sticky lg:top-28 self-start">
          <Reveal>
            <Eyebrow light>The Signature Differentiator</Eyebrow>
            <h2 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.1]">
              Don't just learn accounting.{" "}
              <span className="text-[#C59B27]">Practice it.</span>
            </h2>
            <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-md">
              Every capstone student acts as Chief Accountant for a simulated trading business across a full
              financial year — from company creation to a defended audit review.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-2 text-xs font-mono-sn font-bold tracking-widest" data-testid="capstone-journey-strip">
              {["LEARN", "PRACTICE", "APPLY", "BUILD CONFIDENCE"].map((s, i) => (
                <span key={s} className="flex items-center gap-2">
                  <span className={i === 3 ? "text-[#C59B27]" : "text-slate-300"}>{s}</span>
                  {i < 3 && <ArrowRight size={12} className="text-[#0F5132]" />}
                </span>
              ))}
            </div>
            <EnquiryModal
              source="capstone-section"
              trigger={
                <button data-testid="capstone-cta-btn" className="mt-10 group flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C59B27] text-[#0A192F] font-display font-bold text-sm hover:bg-[#d8ae3a] transition-colors">
                  Experience Practical Learning
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              }
            />
          </Reveal>
        </div>
        <div className="relative">
          <div className="absolute left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-[#C59B27]/60 via-white/15 to-transparent" />
          <div className="space-y-2">
            {CAPSTONE_STEPS.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.05}>
                <div
                  data-testid={`capstone-step-${s.step}`}
                  className="group relative flex gap-6 p-4 rounded-xl hover:bg-white/5 transition-colors duration-300"
                >
                  <div className="relative z-10 w-14 shrink-0">
                    <span className="font-mono-sn font-bold text-2xl text-[#C59B27]/80 group-hover:text-[#C59B27] transition-colors">
                      {s.step}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-white text-base sm:text-lg">{s.name}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed mt-1">{s.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CareerPathways = () => (
  <section className="py-20 lg:py-28" data-testid="career-pathways-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center max-w-2xl mx-auto">
        <Eyebrow>Career Pathways</Eyebrow>
        <h2 className="font-display font-extrabold tracking-tight text-[#0A192F] text-2xl sm:text-3xl lg:text-4xl mt-3">
          Build skills for real accounting roles
        </h2>
        <p className="mt-4 text-sm sm:text-base text-[#486581]">
          No inflated promises — just skills mapped to roles businesses actually hire for.
          Indicative market ranges shown for guidance.
        </p>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {CAREER_ROLES.map((role, i) => (
          <Reveal key={role.title} delay={i * 0.05}>
            <div
              data-testid={`career-role-${i}`}
              className="h-full bg-white rounded-2xl border border-slate-200 p-5 hover:border-[#C59B27]/60 hover:shadow-lg transition-all duration-300"
            >
              <h3 className="font-display font-bold text-[#0A192F] text-base">{role.title}</h3>
              <p className="font-mono-sn text-xs font-bold text-[#0F5132] mt-2">{role.salary}</p>
              <p className="text-xs text-[#627D98] mt-2 leading-relaxed">{role.skills}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="text-center mt-10">
        <Link
          to="/courses"
          data-testid="career-explore-courses-btn"
          className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#0A192F] text-white font-display font-bold text-sm hover:bg-[#0F5132] transition-colors"
        >
          Explore Career-Focused Courses <ArrowRight size={16} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ServicesSection = ({ services }) => (
  <section className="py-16 lg:py-20 bg-[#F1F5F9] border-y border-slate-200" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Professional Financial Services</Eyebrow>
          <h2 className="font-display font-extrabold tracking-tight text-[#0A192F] text-xl sm:text-2xl lg:text-3xl mt-3">
            Beyond training — a full financial services wing.
          </h2>
        </div>
        <Link to="/services" data-testid="explore-services-link" className="flex items-center gap-1.5 text-sm font-bold text-[#0F5132] hover:gap-3 transition-all">
          Explore Professional Services <ArrowRight size={16} />
        </Link>
      </Reveal>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s, i) => (
          <Reveal key={s.id} delay={i * 0.06}>
            <Link
              to={`/services/${s.slug}`}
              data-testid={`service-card-${s.slug}`}
              className="group flex items-start justify-between gap-4 bg-white rounded-xl border border-slate-200 p-5 hover:border-[#0F5132]/50 hover:shadow-md transition-all duration-300"
            >
              <div>
                <h3 className="font-display font-bold text-[#0A192F] text-sm sm:text-base">{s.name}</h3>
                <p className="text-xs text-[#627D98] leading-relaxed mt-1.5">{s.short_desc}</p>
              </div>
              <ArrowRight size={16} className="text-[#C59B27] shrink-0 mt-1 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TestimonialsSection = ({ items }) => (
  <section className="py-20 lg:py-28" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <Eyebrow>Student Voices</Eyebrow>
        <h2 className="font-display font-extrabold tracking-tight text-[#0A192F] text-2xl sm:text-3xl lg:text-4xl mt-3">
          Genuine words from genuine learners.
        </h2>
      </Reveal>
      {items.length === 0 ? (
        <Reveal className="mt-12">
          <div className="bg-white rounded-2xl border border-dashed border-[#C59B27]/60 p-10 text-center max-w-2xl" data-testid="testimonials-empty">
            <Quote size={28} className="text-[#C59B27] mx-auto" />
            <p className="mt-4 text-[#486581] text-sm leading-relaxed">
              Our first batch of capstone graduates is in the making. We publish only real, verifiable student
              stories — never fabricated ones. Yours could be the first.
            </p>
            <Link to="/courses" className="inline-block mt-6 px-6 py-3 rounded-full bg-[#0A192F] text-white text-sm font-bold hover:bg-[#0F5132] transition-colors">
              Start Your Story
            </Link>
          </div>
        </Reveal>
      ) : (
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t, i) => (
            <Reveal key={t.id} delay={i * 0.08}>
              <div className="h-full bg-white rounded-2xl border border-slate-200 p-7" data-testid={`testimonial-card-${i}`}>
                <Quote size={22} className="text-[#C59B27]" />
                <p className="mt-4 text-sm text-[#334E68] leading-relaxed italic">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-[#0A192F] flex items-center justify-center text-[#C59B27] font-display font-bold text-sm">
                      {t.name?.[0]}
                    </div>
                  )}
                  <div>
                    <p className="font-display font-bold text-sm text-[#0A192F]">{t.name}</p>
                    <p className="text-xs text-[#627D98]">{t.role}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  </section>
);

const FaqSection = ({ faqs }) => (
  <section className="py-20 lg:py-24 bg-white border-y border-slate-200" data-testid="faq-section">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center">
        <Eyebrow>Questions, Answered</Eyebrow>
        <h2 className="font-display font-extrabold tracking-tight text-[#0A192F] text-2xl sm:text-3xl lg:text-4xl mt-3">
          Frequently asked questions
        </h2>
      </Reveal>
      <Reveal className="mt-10">
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.slice(0, 6).map((f, i) => (
            <AccordionItem
              key={f.id || i}
              value={`faq-${i}`}
              className="bg-[#F8FAFC] border border-slate-200 rounded-xl px-5"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="font-display font-bold text-sm text-[#0A192F] text-left hover:no-underline">
                {f.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-[#486581] leading-relaxed">{f.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="py-20 lg:py-28 bg-[#0A192F] relative overflow-hidden" data-testid="final-cta-section">
    <div className="absolute inset-0 hero-grid" />
    <div className="absolute -bottom-40 left-1/2 -translate-x-1/2 w-[700px] h-[400px] rounded-full bg-[#C59B27]/15 blur-[130px]" />
    <Reveal className="relative max-w-3xl mx-auto px-4 text-center">
      <h2 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1]">
        Ready to build practical accounting skills?
      </h2>
      <p className="mt-6 text-slate-300 text-base leading-relaxed max-w-xl mx-auto">
        Whether you're starting your career, upgrading your accounting skills or looking for professional financial
        assistance — SterlingNest can help you take the next step.
      </p>
      <div className="mt-10 flex flex-wrap justify-center gap-4">
        <Link
          to="/courses"
          data-testid="final-join-course-btn"
          className="px-8 py-4 rounded-full bg-[#C59B27] text-[#0A192F] font-display font-bold text-sm hover:bg-[#d8ae3a] hover:scale-[1.03] transition-all"
        >
          Join a Course
        </Link>
        <a
          href={waLink("Hi, I would like to talk to SterlingNest about courses and services.")}
          target="_blank"
          rel="noreferrer"
          data-testid="final-talk-btn"
          className="px-8 py-4 rounded-full border border-white/30 text-white font-display font-bold text-sm hover:border-[#C59B27] hover:text-[#C59B27] transition-colors"
        >
          Talk to Us
        </a>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  useTitle("");
  const [courses, setCourses] = useState([]);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/courses?featured=true").then((r) => setCourses(r.data.slice(0, 4))).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero courses={courses} />
      <Marquee />
      <WhySection />
      <FeaturedCourses courses={courses} />
      <Capstone />
      <CareerPathways />
      <ServicesSection services={services} />
      <TestimonialsSection items={testimonials} />
      <FaqSection faqs={faqs} />
      <FinalCta />
    </div>
  );
}
